import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  TrendingUp,
  FileText,
  ArrowLeftRight,
  Wallet,
  Users,
  ChevronLeft,
  Sun,
  Moon,
  LogOut,
  Scale,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { languages, type LanguageCode } from '../../lib/i18n';

// SVG Flag Components
function USFlag() {
  return (
    <svg width="20" height="14" viewBox="0 0 20 14">
      <rect width="20" height="14" fill="#B22234"/>
      <rect y="1.08" width="20" height="1.08" fill="white"/>
      <rect y="3.23" width="20" height="1.08" fill="white"/>
      <rect y="5.38" width="20" height="1.08" fill="white"/>
      <rect y="7.54" width="20" height="1.08" fill="white"/>
      <rect y="9.69" width="20" height="1.08" fill="white"/>
      <rect y="11.85" width="20" height="1.08" fill="white"/>
      <rect width="8" height="7.54" fill="#3C3B6E"/>
    </svg>
  );
}

function RUFlag() {
  return (
    <svg width="20" height="14" viewBox="0 0 20 14">
      <rect width="20" height="4.67" fill="white"/>
      <rect y="4.67" width="20" height="4.67" fill="#0039A6"/>
      <rect y="9.33" width="20" height="4.67" fill="#D52B1E"/>
    </svg>
  );
}

function ESFlag() {
  return (
    <svg width="20" height="14" viewBox="0 0 20 14">
      <rect width="20" height="14" fill="#AA151B"/>
      <rect y="3.5" width="20" height="7" fill="#F1BF00"/>
    </svg>
  );
}

const flagComponents: Record<LanguageCode, () => JSX.Element> = {
  en: USFlag,
  ru: RUFlag,
  es: ESFlag,
};

export interface CashierSidebarProps {
  onLogout: () => void;
  onToggleTheme: () => void;
  theme: 'light' | 'dark';
}

export function CashierSidebar({ onLogout, onToggleTheme, theme }: CashierSidebarProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const currentLang = (i18n.language?.split('-')[0] || 'en') as LanguageCode;

  const changeLanguage = (lang: LanguageCode) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const navItems = [
    { to: '/', icon: TrendingUp, label: t('nav.operations', 'Operations'), end: true },
    { to: '/reports', icon: FileText, label: t('nav.reports', 'Reports') },
    { to: '/transactions', icon: ArrowLeftRight, label: t('nav.transactions', 'Transactions') },
    { to: '/movements', icon: BookOpen, label: t('nav.movements', 'Movements') },
    { to: '/wallets', icon: Wallet, label: t('nav.accounts', 'Accounts') },
    { to: '/admin/daily-balances', icon: Scale, label: t('nav.dailyBalances', 'Daily Balances') },
    { to: '/clients', icon: Users, label: t('nav.clients', 'Clients') },
  ];

  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    const parts = name.split(' ').filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <aside className={`cashier-sidebar ${isCollapsed ? 'collapsed' : ''} ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="logo-icon">
          <LayoutGrid size={24} />
        </div>
        {!isCollapsed && <span className="logo-text">STR Forms</span>}
        <button
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronLeft size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className="nav-item"
            end={item.end}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon size={20} />
            {!isCollapsed && <span className="nav-label">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        {/* Language Flags */}
        <div className="language-flags">
          {languages.map((lang) => {
            const FlagComponent = flagComponents[lang.code];
            return (
              <button
                key={lang.code}
                className={`flag-btn ${currentLang === lang.code ? 'active' : ''}`}
                onClick={() => changeLanguage(lang.code)}
                title={lang.name}
              >
                <FlagComponent />
              </button>
            );
          })}
        </div>

        {/* Theme Toggle */}
        <button
          className="theme-toggle-btn"
          onClick={onToggleTheme}
          title={theme === 'light' ? t('theme.dark', 'Dark mode') : t('theme.light', 'Light mode')}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          {!isCollapsed && (
            <span className="theme-label">
              {theme === 'light' ? t('theme.dark', 'Dark mode') : t('theme.light', 'Light mode')}
            </span>
          )}
        </button>

        {/* User Profile */}
        <div className="user-profile">
          <div className="user-avatar">
            {getInitials(user?.fullName)}
          </div>
          {!isCollapsed && (
            <div className="user-info">
              <span className="cashier-sidebar-user-name">{user?.fullName || 'User'}</span>
              <span className="cashier-sidebar-user-role">{user?.role?.toUpperCase()}</span>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button className="logout-btn" onClick={onLogout} title={t('nav.logout', 'Logout')}>
          <LogOut size={18} />
          {!isCollapsed && <span>{t('nav.logout', 'Logout')}</span>}
        </button>
      </div>

      <style>{`
        .cashier-sidebar {
          width: 220px;
          min-width: 220px;
          height: 100vh;
          background: var(--color-surface);
          border-right: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          z-index: 100;
          transition: width 0.2s ease, min-width 0.2s ease;
        }

        .cashier-sidebar.collapsed {
          width: 72px;
          min-width: 72px;
        }

        /* Logo */
        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 16px;
          position: relative;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          flex-shrink: 0;
        }

        .logo-text {
          font-size: 18px;
          font-weight: 700;
          color: var(--ctp-blue, #3b82f6);
        }

        .collapse-btn {
          position: absolute;
          right: -12px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--color-text-muted);
          transition: all 0.15s;
          z-index: 10;
        }

        .collapse-btn:hover {
          background: var(--color-surface-hover);
          color: var(--color-text);
        }

        .collapsed .collapse-btn {
          transform: translateY(-50%) rotate(180deg);
        }

        /* Navigation */
        .sidebar-nav {
          flex: 1;
          padding: 8px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          color: var(--color-text-muted);
          text-decoration: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.15s;
          position: relative;
        }

        .nav-item:hover {
          color: var(--color-text);
          background: var(--color-surface-hover);
          text-decoration: none;
        }

        .nav-item.active {
          color: var(--ctp-blue, #3b82f6);
          background: rgba(59, 130, 246, 0.1);
        }

        .nav-item.active::before {
          content: '';
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 6px;
          height: 6px;
          background: var(--ctp-blue, #3b82f6);
          border-radius: 50%;
        }

        .collapsed .nav-item {
          padding: 12px;
          justify-content: center;
        }

        .collapsed .nav-item.active::before {
          display: none;
        }

        .nav-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Footer */
        .sidebar-footer {
          padding: 16px;
          border-top: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        /* Language Flags */
        .language-flags {
          display: flex;
          gap: 8px;
          align-items: center;
          justify-content: center;
        }

        .flag-btn {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          padding: 6px 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          border-radius: 6px;
          transition: all 0.15s ease;
          opacity: 0.7;
        }

        .flag-btn:hover {
          background: var(--color-surface-hover);
          border-color: var(--color-text-muted);
          opacity: 1;
        }

        .flag-btn.active {
          background: var(--color-surface-hover);
          border-color: var(--ctp-teal, #14b8a6);
          opacity: 1;
        }

        .flag-btn svg {
          display: block;
          border-radius: 2px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }

        .collapsed .language-flags {
          flex-direction: column;
          gap: 6px;
        }

        /* Theme Toggle Button */
        .theme-toggle-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: var(--color-surface-hover);
          border: none;
          border-radius: 8px;
          color: var(--color-text-muted);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          width: 100%;
        }

        .theme-toggle-btn:hover {
          color: var(--color-text);
          background: var(--color-border);
        }

        .collapsed .theme-toggle-btn {
          justify-content: center;
          padding: 10px;
        }

        .theme-label {
          white-space: nowrap;
        }

        /* User Profile */
        .user-profile {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--ctp-teal) 0%, #115e59 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
        }

        .user-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .cashier-sidebar .cashier-sidebar-user-name {
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cashier-sidebar.theme-light .cashier-sidebar-user-name {
          color: #4c4f69;
        }

        .cashier-sidebar.theme-dark .cashier-sidebar-user-name {
          color: #cdd6f4;
        }

        .cashier-sidebar-user-role {
          font-size: 10px;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .collapsed .user-profile {
          justify-content: center;
        }

        /* Logout Button */
        .logout-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 10px 14px;
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.3);
          border-radius: 8px;
          color: var(--ctp-red, #dc2626);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          width: 100%;
        }

        .logout-btn:hover {
          background: rgba(220, 38, 38, 0.2);
          border-color: var(--ctp-red, #dc2626);
        }

        .collapsed .logout-btn {
          padding: 10px;
        }

        .collapsed .logout-btn span {
          display: none;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .cashier-sidebar {
            width: 72px;
            min-width: 72px;
          }

          .logo-text,
          .nav-label,
          .user-info,
          .theme-label,
          .logout-btn span {
            display: none;
          }

          .logout-btn {
            padding: 10px;
          }

          .nav-item {
            padding: 12px;
            justify-content: center;
          }

          .nav-item.active::before {
            display: none;
          }

          .language-flags {
            flex-direction: column;
          }

          .theme-toggle-btn {
            justify-content: center;
            padding: 10px;
          }

          .user-profile {
            justify-content: center;
          }

          .collapse-btn {
            display: none;
          }
        }
      `}</style>
    </aside>
  );
}
